import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import EmailConfView from '../view/EmailConfView';
import fetchAPI from '../../../../utils/fetchAPI';
import { FormBuilderContext } from '../../../engine/FormBuilderEngine';
import useGlobalVariables from '../../../../hooks/useGlobalVariables';
import getMatchingUsers from '../../../../utils/getMatchingUsers';
import useTranslations from '../../../../hooks/useTranslations';
import { Divider } from '@material-ui/core';
import { isFieldPlaceHolderAllowed } from '../../../engine/EmailDialogEngine';
import { getAllFields } from '../../../../internal-components/form-builder/getAllFields';
import useSkipUpdate from '../../../../hooks/useSkipRender';
import SerializationCopy from '../../../../utils/SerializationCopy';
import { AutomationActionsContext } from '../../engine/AutomationsActionsEngine';

const emails2Options = emails =>
  emails.map(r => ({
    value: r.id,
    text: r.title,
    'data-testid': `select-template-${r.id}`
  }));

const EmailConfReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    case 'update_root':
      return { ...state, root: { ...state.root, ...payload } };
    default:
      return state;
  }
};

const initialReducerState = {
  root: { emailTemplateId: '00000000-0000-0000-0000-000000000019' }
};

// TODO: replace email functionality with ActionEmailTemplateEditor
const EmailConfEngine = ({ update = d => d, variant, ...props }) => {
  const [data, dispatch] = useReducer(EmailConfReducer, initialReducerState);
  const { setIsLoading } = useGlobalVariables();
  const { form, formId } = useContext(FormBuilderContext);
  const { mailTemplates } = useContext(AutomationActionsContext);

  const { shouldSkipUpdate, skipUpdate } = useSkipUpdate();
  const translate = useTranslations();
  const [typesOfRecipient, setTypesOfRecipient] = useState([]);
  const [specificUsers, setSpecificUsers] = useState([]);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [activeTemplateCode, setActiveTemplateCode] = useState(null);
  const [playIconAttentionAnimation, setPlayIconAttentionAnimation] =
    useState(false);
  const [templateIdDDValue, setTemplateIdDDValue] = useState(
    data.root.emailTemplateId
  );
  const [emailTemplatesOptions, setEmailTemplatesOptions] = useState([]);

  const isEmailTemplateOptionsCallMade = useRef(false);

  const forceTemplateCreation = useMemo(
    () => !!(mailTemplates || []).find(temp => temp.id === templateIdDDValue),
    [templateIdDDValue, mailTemplates]
  );

  useEffect(() => {
    if (shouldSkipUpdate()) {
      return;
    }
    update({
      additionalParameters: { root: data.root }
    });
  }, [data]);

  useEffect(() => {
    const root = props.additionalParameters && props.additionalParameters.root;
    if (root) {
      initDataFromRoot(root);
    }
  }, []);

  useEffect(() => {
    if (mailTemplates && mailTemplates.length) {
      if (!isEmailTemplateOptionsCallMade.current) {
        isEmailTemplateOptionsCallMade.current = true;
        const options = emails2Options(mailTemplates);
        setEmailTemplatesOptions(options);
      }
      const formLvlTemplate = mailTemplates.find(
        temp => temp.code === 'automation'
      );
      if (
        data.root.emailTemplateId === '00000000-0000-0000-0000-000000000019'
      ) {
        skipUpdate();
        dispatch({
          type: 'update_root',
          payload: { emailTemplateId: formLvlTemplate.id }
        });
      }
    }
  }, [mailTemplates]);

  useEffect(() => {
    if (isEmailDialogOpen) {
      setTemplateIdDDValue(data.root.emailTemplateId);
    }
  }, [isEmailDialogOpen]);

  useEffect(() => {
    if (isEmailDialogOpen && !isEmailTemplateOptionsCallMade.current) {
      loadEmailTemplatesOptions();
    }
  }, [isEmailDialogOpen]);

  const emailFields = useMemo(() => {
    const allFields = getAllFields(form);
    return allFields.filter(field => field.uiType === 'email_address');
  }, [form]);

  const emailFieldId =
    data.root &&
    data.root.to?.find(t => t.type === 'user_from_field')?.emailFieldId;

  const initDataFromRoot = useCallback(_root => {
    const root = SerializationCopy(_root);
    root.emailTemplateId = root.emailTemplateId || data.root.emailTemplateId;
    setTemplateIdDDValue(root.emailTemplateId);

    const to = root.to || [];
    const newTypesOfRecipient = to.map(t => t.type);
    setTypesOfRecipient(newTypesOfRecipient);
    // setIsEmailDialogOpen(!!root.openDialog);
    setPlayIconAttentionAnimation(!!root.openDialog);

    const usersBlockIndex = to.findIndex(t => t.type === 'specificUser');
    const isBlockExists = usersBlockIndex >= 0;

    if (isBlockExists) {
      fetchAPI('/users/v1/bulkfetch/search', {
        method: 'POST',
        body: {
          queryDescriptor: { elements: to[usersBlockIndex].userIds }
        }
      }).then(({ results }) => {
        const specificUsers = results.map(u => ({
          label: `${u.info.firstName} ${u.info.lastName} <${u.info.email}>`,
          value: u.id
        }));

        setSpecificUsers(specificUsers);
      });
    }

    delete root.openDialog;

    skipUpdate();
    dispatch({ type: 'update_root', payload: root });
  }, []);

  const loadEmailTemplatesOptions = useCallback(() => {
    setIsLoading(true);
    fetchAPI(
      `/mailer/v1/emails?assetId=${formId}&assetType=forms&category=forms`
    )
      .then(response => {
        const options = emails2Options(
          response.results.filter(template => !template?.personalMsg)
        );
        setEmailTemplatesOptions(options);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const getSelectUsersOptions = useCallback(v => {
    return getMatchingUsers(v).then(options => {
      options.unshift({ isSectionBreak: true, text: <Divider /> });
      //   options.unshift({
      //     text: t('automaions.wizard.actions.email.recipient.sharedUsers'),
      //     value: {
      //       value: '???',
      //       data: {
      //         firstName: t(
      //           'automaions.wizard.actions.email.recipient.sharedUsers'
      //         ),
      //         lastName: ''
      //       }
      //     }
      //   });
      //   options.unshift({
      //     text: t('automaions.wizard.actions.email.recipient.responsibleAdmin'),
      //     value: {
      //       value: '??',
      //       data: {
      //         firstName: t(
      //           'automaions.wizard.actions.email.recipient.responsibleAdmin'
      //         ),
      //         lastName: ''
      //       }
      //     }
      //   });
      options.unshift({
        text: translate(
          'automaions.wizard.actions.email.recipient.assignedUser'
        ),
        value: {
          value: null,
          data: {
            firstName: translate(
              'automaions.wizard.actions.email.recipient.assignedUser'
            ),
            lastName: ''
          }
        }
      });

      return options;
    });
  }, []);

  const onTypesOfRecipientChange = useCallback(
    types => {
      setTypesOfRecipient(types);
      const to = (types || [])
        .map(t => {
          if (t === 'assignedUser') {
            return { type: 'assignedUser' };
          }
          if (t === 'specificUser' && (specificUsers || []).length) {
            return {
              type: 'specificUser',
              userIds: specificUsers.map(u => u.value)
            };
          }
          if (t === 'user_from_field') {
            return { type: 'user_from_field' };
          }
          if (t === 'user_from_lookup') {
            return { type: 'user_from_lookup' };
          }
          return null;
        })
        .filter(t => t);
      dispatch({ type: 'update_root', payload: { to } });
    },
    [specificUsers, update]
  );

  const onEmailReducerChange = useCallback(myData => {
    setActiveTemplateCode(myData.code);
  }, []);

  const getAdditionalPlaceHolders = () => {
    const allFields = getAllFields(form);
    const additionalPlaceHolders = allFields.map(field => ({
      value: field.internalId,
      text: field.title || `Untitled ${field.uiType} field`,
      'data-testid': `placeholder-field-${field.internalId}`
    }));
    additionalPlaceHolders.push({
      value: 'referenceId',
      text: 'referenceId',
      'data-testid': `placeholder-field-child.referenceId`
    });
    return additionalPlaceHolders;
  };

  const fieldPlaceHolders = useMemo(() => {
    return isFieldPlaceHolderAllowed(activeTemplateCode)
      ? getAdditionalPlaceHolders()
      : [];
  }, [activeTemplateCode]);

  const onTemplateSaved = useCallback(tId => {
    dispatch({ type: 'update_root', payload: { emailTemplateId: tId } });
    setTemplateIdDDValue(tId);
  }, []);

  const onEmailDialogClosed = useCallback(() => {
    setIsEmailDialogOpen(false);
  }, []);

  const onUsersSelectChange = useCallback(
    v => {
      setSpecificUsers(v);

      const to = SerializationCopy(data.root.to || []);
      const usersBlockIndex = to.findIndex(t => t.type === 'specificUser');

      const isBlockExists = usersBlockIndex >= 0;
      const isValueExists = v && !!v.length;
      const plainList = isValueExists ? v.map(u => u.value) : []; // list in a format we send to the b/e (just list of ids)

      if (isBlockExists) {
        // we have block with data regarding the specific users in "to"
        if (isValueExists) {
          // current value is not empty
          // update block with current value
          to[usersBlockIndex] = { type: 'specificUser', userIds: plainList };
        } else {
          // remove the block
          to.splice(usersBlockIndex, 1);
        }
      } else {
        if (isValueExists) {
          // create the block with current users value
          to.push({ type: 'specificUser', userIds: plainList });
        }
        // else we do nothing - block doesn't exist and we don't have users ¯\_(ツ)_/¯
      }
      dispatch({ type: 'update_root', payload: { to } });
    },
    [data]
  );

  const onChooseUserEmailChange = useCallback(
    v => {
      if (v) {
        const to = SerializationCopy(data.root.to || []);
        const usersBlockIndex = to.findIndex(t => t.type === 'user_from_field');
        to[usersBlockIndex] = { type: 'user_from_field' };
        to.splice(usersBlockIndex, 1);
        to.push({ type: 'user_from_field', emailFieldId: v });
        dispatch({ type: 'update_root', payload: { to } });
      }
    },
    [data]
  );

  const onUpdateSelectUsersEngine = value => {
    dispatch({
      type: 'update_root',
      payload: value
    });
  };

  const viewProps = {
    ...props,
    variant,
    emailTemplatesOptions,
    getSelectUsersOptions,
    typesOfRecipient,
    showUserSelectionDD:
      typesOfRecipient && typesOfRecipient.includes('specificUser'),
    specificUsers,
    setSpecificUsers,
    isEmailDialogOpen,
    setIsEmailDialogOpen,
    templateIdDDValue,
    setTemplateIdDDValue,
    templateId: data.root.emailTemplateId,
    setTemplateId: tId =>
      dispatch({ type: 'update_root', payload: { emailTemplateId: tId } }),
    onEmailReducerChange,
    fieldPlaceHolders,
    onTypesOfRecipientChange,
    onTemplateSaved,
    onEmailDialogClosed,
    onUsersSelectChange,
    playIconAttentionAnimation,
    disabled: props.disabled,
    forceTemplateCreation,
    emailFields,
    emailFieldId,
    showEmailFields:
      typesOfRecipient && typesOfRecipient.includes('user_from_field'),
    showEmailFromLookup:
      typesOfRecipient && typesOfRecipient.includes('user_from_lookup'),
    onChooseUserEmailChange,
    onUpdateSelectUsersEngine
  };

  return <EmailConfView {...viewProps} />;
};

export default EmailConfEngine;
